import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  QrCode,
  Copy,
  Check,
  X,
  ShieldCheck,
  Sparkles,
  ShieldAlert,
  Users,
  RefreshCw,
  Plus,
  Trash2,
  Clock,
  Laptop,
} from 'lucide-react';

interface MagicPass {
  id: number;
  passId: string;
  label: string;
  maxDevices: number;
  boundDevicesCount: number;
  boundDevices: Array<{ ip: string; userAgent: string; firstSeenAt: string; lastActiveAt: string }>;
  status: 'active' | 'revoked' | 'expired';
  expiresAt: string;
  createdAt: string;
  targetPhoneNumber?: string | null;
  targetPhoneNumberId?: string | null;
  token: string;
  magicUrl: string;
}

interface KangavenaWhatsAppMobileModalProps {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  tenant?: any;
  connectedInstances?: any[];
}

export const KangavenaWhatsAppMobileModal: React.FC<KangavenaWhatsAppMobileModalProps> = ({
  isOpen,
  onClose,
  token = '',
  tenant,
  connectedInstances = [],
}) => {
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [maxDevices, setMaxDevices] = useState<number>(1);
  const [passLabel, setPassLabel] = useState<string>('Staff Mobile Access');
  const [targetAccount, setTargetAccount] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState<number>(30);
  const [generating, setGenerating] = useState<boolean>(false);
  const [createdPass, setCreatedPass] = useState<MagicPass | null>(null);
  const [passes, setPasses] = useState<MagicPass[]>([]);
  const [internalInstances, setInternalInstances] = useState<any[]>([]);
  const [loadingPasses, setLoadingPasses] = useState<boolean>(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const effectiveToken =
    token ||
    (typeof window !== 'undefined'
      ? localStorage.getItem('tenet_client_token') || sessionStorage.getItem('tenet_client_token') || ''
      : '');

  // Resolve available instances from props or fetch
  const availableInstances = (connectedInstances && connectedInstances.length > 0) ? connectedInstances : internalInstances;

  // Fetch connected instances if not provided in props
  const fetchInstances = async () => {
    if (!effectiveToken || (connectedInstances && connectedInstances.length > 0)) return;
    try {
      const res = await fetch('/api/tenet/live-chats', {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.connectedInstances)) {
        setInternalInstances(data.connectedInstances);
      }
    } catch (err) {
      console.warn('[MagicPassModal] Failed to fetch instances:', err);
    }
  };

  // Fetch active magic passes for this tenant
  const fetchPasses = async () => {
    if (!effectiveToken) return;
    try {
      setLoadingPasses(true);
      const res = await fetch('/api/tenet/magic-passes', {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.passes)) {
        setPasses(data.passes);
      }
    } catch (err) {
      console.error('[MagicPassModal] Fetch error:', err);
    } finally {
      setLoadingPasses(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPasses();
      fetchInstances();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = (text: string, identifier: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(identifier);
    setTimeout(() => setCopiedToken(null), 2500);
  };

  // Generate new restricted pass with device limit & optional account scope
  const handleGeneratePass = async () => {
    if (!effectiveToken) return;
    try {
      setGenerating(true);
      const selectedInst = availableInstances.find(
        (i) => i.phoneNumber === targetAccount || i.metaPhoneNumberId === targetAccount
      );
      const targetPhoneNumber = selectedInst?.phoneNumber || (targetAccount && !targetAccount.startsWith('1') ? targetAccount : null);
      const targetPhoneNumberId = selectedInst?.metaPhoneNumberId || (targetAccount && /^\d{10,20}$/.test(targetAccount) ? targetAccount : null);

      const res = await fetch('/api/tenet/magic-passes/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: JSON.stringify({
          label: passLabel.trim() || 'Staff Mobile Access',
          maxDevices,
          expiresInDays,
          targetPhoneNumber,
          targetPhoneNumberId,
        }),
      });
      const data = await res.json();
      if (data.success && data.pass) {
        setCreatedPass(data.pass);
        fetchPasses();
      } else {
        alert(data.error || 'Failed to generate access link');
      }
    } catch (err) {
      console.error('[MagicPassModal] Create error:', err);
      alert('Network error while generating access pass');
    } finally {
      setGenerating(false);
    }
  };

  // Revoke active pass
  const handleRevokePass = async (passId: string) => {
    if (!confirm('Are you sure you want to revoke this access link? All connected devices will be disconnected immediately.')) {
      return;
    }
    try {
      setRevokingId(passId);
      const res = await fetch('/api/tenet/magic-passes/revoke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: JSON.stringify({ passId }),
      });
      const data = await res.json();
      if (data.success) {
        if (createdPass?.passId === passId) {
          setCreatedPass(null);
        }
        fetchPasses();
      } else {
        alert(data.error || 'Failed to revoke link');
      }
    } catch (err) {
      console.error('[MagicPassModal] Revoke error:', err);
    } finally {
      setRevokingId(null);
    }
  };

  const activePassToDisplay = createdPass || (passes.length > 0 && passes[0].status === 'active' ? passes[0] : null);
  const activeQrUrl = activePassToDisplay
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(activePassToDisplay.magicUrl)}&bgcolor=11-27-33&color=00-a8-84&margin=10`
    : null;

  return (
    <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 selection:bg-[#00a884] selection:text-white">
      <div className="bg-[#111b21] border border-[#222d34] rounded-3xl max-w-xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
        {/* Top Emerald Accent Line */}
        <div className="h-1 bg-gradient-to-r from-emerald-500 via-[#00a884] to-teal-400 shrink-0" />

        {/* Modal Header */}
        <div className="px-6 py-4 bg-[#202c33] border-b border-[#2a3942] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#00a884]/20 border border-[#00a884]/40 flex items-center justify-center text-[#00a884] shadow-md shrink-0">
              <Smartphone size={20} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <span>WhatsApp Mobile Access Pass Manager</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#00a884]/20 text-[#00a884] border border-[#00a884]/30 font-semibold">
                  RESTRICTED PASS
                </span>
              </h3>
              <p className="text-xs text-[#8696a0]">
                {tenant?.name || 'Your Store'} • Enforce strict device limits & 1-click revocation
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-[#374248] text-[#8696a0] hover:text-white transition cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs: Create New vs Active Passes */}
        <div className="px-6 pt-3 bg-[#111b21] border-b border-[#222d34] flex items-center gap-4 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('create')}
            className={`pb-2.5 text-xs font-bold transition border-b-2 cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'create'
                ? 'border-[#00a884] text-[#00a884]'
                : 'border-transparent text-[#8696a0] hover:text-white'
            }`}
          >
            <Plus size={14} />
            <span>Generate Bound Link</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('manage');
              fetchPasses();
            }}
            className={`pb-2.5 text-xs font-bold transition border-b-2 cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'manage'
                ? 'border-[#00a884] text-[#00a884]'
                : 'border-transparent text-[#8696a0] hover:text-white'
            }`}
          >
            <Users size={14} />
            <span>Active Links & Connected Devices ({passes.filter((p) => p.status === 'active').length})</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 text-[#d1d7db] text-xs">
          {activeTab === 'create' && (
            <>
              {/* Device Limit & Config Form */}
              <div className="p-4 rounded-2xl bg-[#0c1317] border border-[#222d34] space-y-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#8696a0] block mb-1.5">
                    1. Max Allowed Devices (Device Lock Limit)
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 5].map((count) => (
                      <button
                        key={count}
                        type="button"
                        onClick={() => setMaxDevices(count)}
                        className={`p-2 rounded-xl text-center border font-bold transition cursor-pointer ${
                          maxDevices === count
                            ? 'bg-[#00a884]/20 border-[#00a884] text-[#00a884] shadow-sm'
                            : 'bg-[#202c33] border-[#2a3942] text-[#8696a0] hover:text-white'
                        }`}
                      >
                        <div className="text-sm">{count} Device{count > 1 ? 's' : ''}</div>
                        <div className="text-[9px] font-normal opacity-80">
                          {count === 1 ? '🔒 Most Secure' : `${count} Max IPs`}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#8696a0] block mb-1.5">
                      Pass Label / Recipient
                    </label>
                    <input
                      type="text"
                      value={passLabel}
                      onChange={(e) => setPassLabel(e.target.value)}
                      placeholder="e.g. Cashier iPhone, Manager Phone"
                      className="w-full bg-[#202c33] border border-[#2a3942] rounded-xl px-3 py-2 text-xs text-white placeholder-[#8696a0] focus:outline-none focus:border-[#00a884]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#8696a0] block mb-1.5">
                      Validity Period
                    </label>
                    <select
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(Number(e.target.value))}
                      className="w-full bg-[#202c33] border border-[#2a3942] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-[#00a884]"
                    >
                      <option value={7}>7 Days</option>
                      <option value={30}>30 Days (Recommended)</option>
                      <option value={90}>90 Days</option>
                    </select>
                  </div>
                </div>

                {/* Target WhatsApp Account / Channel Selector */}
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#8696a0] block mb-1.5 flex items-center justify-between">
                    <span>Target WhatsApp Number / Channel</span>
                    <span className="text-[10px] text-[#00a884] font-semibold lowercase">
                      {targetAccount ? 'Scoped access' : 'Full access'}
                    </span>
                  </label>
                  <select
                    value={targetAccount}
                    onChange={(e) => setTargetAccount(e.target.value)}
                    className="w-full bg-[#202c33] border border-[#2a3942] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-[#00a884] cursor-pointer"
                  >
                    <option value="">🌐 All Connected Numbers (Unified Store Access)</option>
                    {availableInstances.map((inst, idx) => {
                      const rawDigits = (inst.phoneNumber || '').replace(/\D/g, '');
                      const displayPhone = rawDigits ? `+${rawDigits}` : (inst.metaPhoneNumberId || `Account ${idx + 1}`);
                      const icon = inst.isPersonal ? '📱 ' : '🏢 ';
                      return (
                        <option
                          key={inst.metaPhoneNumberId || inst.phoneNumber || idx}
                          value={inst.metaPhoneNumberId || inst.phoneNumber}
                        >
                          {icon} {inst.label || displayPhone} {inst.phoneNumber ? `(${displayPhone})` : ''}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-[10px] text-[#8696a0] mt-1">
                    {targetAccount
                      ? '🔒 Strict Isolation: Mobile devices using this link will ONLY be able to view and reply to chats from this specific WhatsApp number.'
                      : '🔓 Unified Inbox: Mobile devices using this link will have access to all connected WhatsApp numbers.'}
                  </p>
                </div>

                <button
                  type="button"
                  disabled={generating}
                  onClick={handleGeneratePass}
                  className="w-full py-2.5 rounded-xl bg-[#00a884] hover:bg-[#00a884]/90 disabled:opacity-50 text-[#111b21] font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg active:scale-95"
                >
                  {generating ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      <span>Generating Bound Pass...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      <span>Generate Secure Magic Pass ({maxDevices} Device Limit)</span>
                    </>
                  )}
                </button>
              </div>

              {/* Display Generated Pass */}
              {activePassToDisplay && activeQrUrl ? (
                <div className="p-4 rounded-2xl bg-[#0c1317] border border-[#00a884]/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white flex items-center gap-1.5 truncate mr-2">
                      <ShieldCheck size={14} className="text-[#00a884] shrink-0" />
                      <span className="truncate">Active Access Pass: {activePassToDisplay.label}</span>
                      {activePassToDisplay.targetPhoneNumber ? (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 shrink-0">
                          📱 Scoped: +{activePassToDisplay.targetPhoneNumber.replace(/\D/g, '')}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shrink-0">
                          🌐 All Numbers
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#00a884]/20 text-[#00a884] border border-[#00a884]/40 font-bold">
                      {activePassToDisplay.boundDevicesCount} / {activePassToDisplay.maxDevices} Device Bound
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="p-2 bg-[#202c33] border border-[#2a3942] rounded-2xl shadow-lg shrink-0">
                      <img
                        src={activeQrUrl}
                        alt="WhatsApp Magic QR Code"
                        className="w-28 h-28 rounded-xl object-contain"
                        loading="eager"
                      />
                    </div>
                    <div className="space-y-2 w-full min-w-0">
                      <p className="text-[11px] text-[#8696a0]">
                        Scan this QR code with the target phone camera, or copy the direct link below. Once opened, it will bind to that device's IP.
                      </p>
                      <div className="flex items-center gap-2 bg-[#202c33] p-1.5 rounded-xl border border-[#2a3942]">
                        <input
                          type="text"
                          readOnly
                          value={activePassToDisplay.magicUrl}
                          className="w-full bg-transparent px-2 text-[11px] text-[#d1d7db] font-mono truncate focus:outline-none select-all"
                        />
                        <button
                          type="button"
                          onClick={() => handleCopy(activePassToDisplay.magicUrl, activePassToDisplay.passId)}
                          className="px-3 py-1.5 rounded-lg bg-[#00a884] hover:bg-[#00a884]/90 text-[#111b21] font-bold text-xs flex items-center gap-1 shrink-0 transition cursor-pointer"
                        >
                          {copiedToken === activePassToDisplay.passId ? (
                            <>
                              <Check size={13} />
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy size={13} />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-[#8696a0]">
                        <span>Expires: {new Date(activePassToDisplay.expiresAt).toLocaleDateString()}</span>
                        <button
                          type="button"
                          onClick={() => handleRevokePass(activePassToDisplay.passId)}
                          className="text-rose-400 hover:text-rose-300 font-bold flex items-center gap-1 cursor-pointer"
                        >
                          <Trash2 size={11} />
                          <span>Revoke Pass</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Zero-Leak Security Notice */}
              <div className="p-3 rounded-xl bg-[#202c33]/40 border border-[#2a3942] flex items-start gap-2.5 text-[11px] text-[#8696a0]">
                <ShieldAlert size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <p>
                  <strong className="text-white">Anti-Leak Protection:</strong> Anyone using this link will only see the live WhatsApp inbox. They <strong className="text-amber-300">cannot</strong> view passwords, manage billing, or generate other links.
                </p>
              </div>
            </>
          )}

          {activeTab === 'manage' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-[#8696a0]">
                  Issued Magic Links & Connected Devices
                </span>
                <button
                  type="button"
                  onClick={fetchPasses}
                  className="p-1 text-[#8696a0] hover:text-white rounded-lg"
                  title="Refresh List"
                >
                  <RefreshCw size={13} className={loadingPasses ? 'animate-spin' : ''} />
                </button>
              </div>

              {loadingPasses ? (
                <div className="p-8 text-center text-[#8696a0]">
                  <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-[#00a884]" />
                  <span>Loading active links...</span>
                </div>
              ) : passes.length === 0 ? (
                <div className="p-6 text-center text-[#8696a0] bg-[#0c1317] rounded-2xl border border-[#222d34]">
                  <span>No magic links generated yet. Use the "Generate Bound Link" tab above to create one.</span>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {passes.map((p) => {
                    const isRevoked = p.status === 'revoked';
                    const isExpired = p.status === 'expired' || new Date(p.expiresAt) < new Date();
                    return (
                      <div
                        key={p.passId}
                        className={`p-3.5 rounded-2xl border transition ${
                          isRevoked
                            ? 'bg-[#0c1317]/50 border-rose-900/40 opacity-60'
                            : isExpired
                            ? 'bg-[#0c1317]/50 border-amber-900/40 opacity-70'
                            : 'bg-[#0c1317] border-[#222d34] hover:border-[#00a884]/40'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-bold text-white text-xs mr-2">{p.label}</span>
                            {p.targetPhoneNumber ? (
                              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 font-bold mr-1.5">
                                📱 +{p.targetPhoneNumber.replace(/\D/g, '')}
                              </span>
                            ) : (
                              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-bold mr-1.5">
                                🌐 All Numbers
                              </span>
                            )}
                            <span
                              className={`text-[9px] font-mono px-2 py-0.5 rounded-full font-bold uppercase ${
                                isRevoked
                                  ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                  : isExpired
                                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                  : 'bg-[#00a884]/20 text-[#00a884] border border-[#00a884]/30'
                              }`}
                            >
                              {p.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-[#8696a0]">
                              {p.boundDevicesCount} / {p.maxDevices} Devices
                            </span>
                            {!isRevoked && (
                              <button
                                type="button"
                                disabled={revokingId === p.passId}
                                onClick={() => handleRevokePass(p.passId)}
                                className="px-2.5 py-1 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-[11px] font-bold transition flex items-center gap-1 cursor-pointer border border-rose-500/30 active:scale-95"
                              >
                                {revokingId === p.passId ? (
                                  <RefreshCw size={11} className="animate-spin" />
                                ) : (
                                  <Trash2 size={11} />
                                )}
                                <span>Revoke</span>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Bound Device Details */}
                        {p.boundDevices && p.boundDevices.length > 0 ? (
                          <div className="mt-2 pt-2 border-t border-[#222d34] space-y-1">
                            <span className="text-[10px] text-[#8696a0] font-bold block">Connected Devices / IPs:</span>
                            {p.boundDevices.map((dev, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-[10px] font-mono bg-[#111b21] px-2 py-1 rounded-lg text-[#8696a0]"
                              >
                                <span className="flex items-center gap-1.5 text-white">
                                  <Laptop size={11} className="text-[#00a884]" />
                                  <span>{dev.ip}</span>
                                </span>
                                <span>Active: {new Date(dev.lastActiveAt).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-[10px] text-[#8696a0] italic">No devices have accessed this link yet.</div>
                        )}

                        {!isRevoked && !isExpired && (
                          <div className="mt-2 flex items-center justify-between text-[11px]">
                            <button
                              type="button"
                              onClick={() => handleCopy(p.magicUrl, p.passId)}
                              className="text-[#00a884] hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                            >
                              {copiedToken === p.passId ? <Check size={12} /> : <Copy size={12} />}
                              <span>{copiedToken === p.passId ? 'Copied URL!' : 'Copy Magic URL'}</span>
                            </button>
                            <span className="text-[10px] text-[#8696a0]">
                              Created: {new Date(p.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3.5 bg-[#202c33] border-t border-[#2a3942] flex items-center justify-between shrink-0">
          <span className="text-[11px] text-[#8696a0]">
            🔒 Strictly managed via Tenet Master Hub.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-[#00a884] hover:bg-[#00a884]/90 text-[#111b21] font-bold text-xs transition cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
